import { StorageContainer } from '@@partner:types:app'
import React, { useState } from 'react'

import { Button, Table, Tag, Modal, Checkbox } from 'rsuite'
import { SortType } from 'rsuite/esm/Table'

import AppLoader from '@components/common/AppLoader'
import { generatePath, Link } from 'react-router-dom'
import { ROUTE_STORAGE_CONTAINERS_EDIT } from '@@partner:config/routes'
import { useDeleteStorageContainer } from '@@partner:graphql/mutation/storageContainer/deleteStorageContainer.gql'
import { useActivateStorageContainer } from '@@partner:graphql/mutation/storageContainer/activateStorageContainer.gql'
import { useDeactivateStorageContainer } from '@@partner:graphql/mutation/storageContainer/deactivateStorageContainer.gql'
import { CONTAINER_SIZE_LABELS, CONTAINER_TYPE_LABELS } from '../../../constants/storageContainer'
import { CreateOutline, TrashOutline, PrintOutline } from 'react-ionicons'

type StorageContainersTableProps = {
  brandSlug: string
  containers: StorageContainer[]
  loading?: boolean
  onSortColumn?: ((dataKey: string, sortType?: SortType) => void) | undefined
  tableSort: {
    fieldName: string
    direction: number
    sortType: SortType
  }
  onRefresh?: () => void
  selectedContainers?: string[]
  onSelectionChange?: (selectedIds: string[]) => void
}

const Column = Table.Column
const HeaderCell = Table.HeaderCell
const Cell = Table.Cell

export default function StorageContainersTable ({
  brandSlug,
  containers = [],
  loading = false,
  onSortColumn,
  tableSort,
  onRefresh,
  selectedContainers = [],
  onSelectionChange
}: StorageContainersTableProps) {

  const [ deleteContainer ] = useDeleteStorageContainer()
  const [ activateContainer ] = useActivateStorageContainer()
  const [ deactivateContainer ] = useDeactivateStorageContainer()
  
  const [ showDeleteModal, setShowDeleteModal ] = useState(false)
  const [ containerToDelete, setContainerToDelete ] = useState<StorageContainer | null>(null)

  const handleCheckboxChange = (containerId: string, checked: boolean) => {
    if (!onSelectionChange) return
    
    if (checked) {
      onSelectionChange([ ...selectedContainers, containerId ])
    }
    else {
      onSelectionChange(selectedContainers.filter(id => id !== containerId))
    }
  }

  const handleCheckAll = (checked: boolean) => {
    if (!onSelectionChange) return
    
    if (checked) {
      onSelectionChange(containers.map(c => String(c.id)))
    }
    else {
      onSelectionChange([])
    }
  }

  const isChecked = (containerId: string) => selectedContainers.includes(containerId)
  const isAllChecked = containers.length > 0 && selectedContainers.length === containers.length
  const isIndeterminate = selectedContainers.length > 0 && selectedContainers.length < containers.length

  const handleDeleteClick = (container: StorageContainer) => {
    setContainerToDelete(container)
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = async () => {
    if (!containerToDelete) return

    try {
      await deleteContainer({
        variables: { id: containerToDelete.id }
      })
      setShowDeleteModal(false)
      setContainerToDelete(null)
      onRefresh?.()
    }
    catch (error) {
      console.error('Error deleting container:', error)
    }
  }

  const handleToggleActive = async (container: StorageContainer) => {
    try {
      if (container.is_active) {
        await deactivateContainer({
          variables: { id: container.id }
        })
      }
      else {
        await activateContainer({
          variables: { id: container.id }
        })
      }
      onRefresh?.()
    }
    catch (error) {
      console.error('Error toggling container status:', error)
    }
  }

  if (loading) {
    return <AppLoader />
  }

  return (
    <>
      <Table
        height={600}
        data={containers}
        sortColumn={tableSort.fieldName}
        sortType={tableSort.sortType}
        onSortColumn={onSortColumn}
      >
        {onSelectionChange && (
          <Column width={60} align="center" fixed>
            <HeaderCell>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Checkbox
                  checked={isAllChecked}
                  indeterminate={isIndeterminate}
                  onChange={(_, checked) => handleCheckAll(checked)}
                />
              </div>
            </HeaderCell>
            <Cell>
              {(rowData: StorageContainer) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Checkbox
                    checked={isChecked(String(rowData.id))}
                    onChange={(_, checked) => handleCheckboxChange(String(rowData.id), checked)}
                  />
                </div>
              )}
            </Cell>
          </Column>
        )}

        <Column width={80} align="center" fixed sortable>
          <HeaderCell>ID</HeaderCell>
          <Cell dataKey="id" />
        </Column>

        <Column width={150} sortable>
          <HeaderCell>Код контейнера</HeaderCell>
          <Cell dataKey="container_id" />
        </Column>

        <Column width={200} sortable>
          <HeaderCell>Название</HeaderCell>
          <Cell dataKey="displayName" />
        </Column>

        <Column width={100} sortable>
          <HeaderCell>Размер</HeaderCell>
          <Cell>
            {(rowData: StorageContainer) => (
              CONTAINER_SIZE_LABELS[rowData.size as keyof typeof CONTAINER_SIZE_LABELS] || rowData.size
            )}
          </Cell>
        </Column>

        <Column width={120} sortable>
          <HeaderCell>Тип</HeaderCell>
          <Cell>
            {(rowData: StorageContainer) => (
              CONTAINER_TYPE_LABELS[rowData.type as keyof typeof CONTAINER_TYPE_LABELS] || rowData.type
            )}
          </Cell>
        </Column>

        <Column width={120} align="center">
          <HeaderCell>Статус</HeaderCell>
          <Cell>
            {(rowData: StorageContainer) => (
              <Tag 
                color={rowData.is_active ? 'green' : 'red'}
                onClick={() => handleToggleActive(rowData)}
                style={{ cursor: 'pointer' }}
              >
                {rowData.is_active ? 'Активен' : 'Неактивен'}
              </Tag>
            )}
          </Cell>
        </Column>

        <Column width={100} align="center">
          <HeaderCell>Заказы</HeaderCell>
          <Cell>
            {(rowData: StorageContainer) => (
              <span>
                {rowData.activeOrdersCount || 0} / {rowData.ordersCount || 0}
              </span>
            )}
          </Cell>
        </Column>

        <Column width={300}>
          <HeaderCell>Описание</HeaderCell>
          <Cell dataKey="description" />
        </Column>

        <Column width={150} align="center">
          <HeaderCell>Создан</HeaderCell>
          <Cell>
            {(rowData: StorageContainer) => (
              new Date(rowData.created_at).toLocaleDateString('ru-RU')
            )}
          </Cell>
        </Column>

        <Column width={180} fixed="right">
          <HeaderCell>Действия</HeaderCell>
          <Cell>
            {(rowData: StorageContainer) => (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <Button
                  size="xs"
                  appearance="ghost"
                  as={Link}
                  to={generatePath(ROUTE_STORAGE_CONTAINERS_EDIT, {
                    brandSlug,
                    containerId: rowData.id
                  })}
                  style={{ marginRight: 8, whiteSpace: 'nowrap' }}
                >
                  <CreateOutline /> Редактировать
                </Button>
                <Button
                  size="xs"
                  appearance="ghost"
                  color="red"
                  onClick={() => handleDeleteClick(rowData)}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <TrashOutline /> Удалить
                </Button>
              </div>
            )}
          </Cell>
        </Column>
      </Table>

      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Подтверждение удаления</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Вы уверены, что хотите удалить контейнер "{containerToDelete?.displayName}"?
          Это действие нельзя отменить.
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowDeleteModal(false)} appearance="subtle">
            Отмена
          </Button>
          <Button onClick={handleConfirmDelete} appearance="primary" color="red">
            Удалить
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
